import useParagraphLineClamped from '@/hooks/useLineClamped';
import { useDynamicScreen, useScreen } from '@/hooks/useScreen';
import {
  EEnterpriseTabKey,
  IEnterpriseProfileStructureJSON,
} from '@/interfaces/enterprise-profile/enterprise.interface';
import {
  EHomepageTabSectionKey,
  TEnterpriseProfileHomepageTabSection,
} from '@/interfaces/enterprise-profile/homepage_tab.interface';
import { IUserProfileData, RoleViewEnum } from '@/interfaces/profile.interface';
import handleShare from 'common/handleShare';
import DSButton from 'design-system/components/Button/Button';
import dynamic from 'next/dynamic';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useModal } from 'state/modal-provider/ModalProvider';
import { OutlinedPencil, OutlinedSharedIcon } from '../../../svgIcon/outlined';
import BlueTick from '../../BlueTick';
import EnterpriseProfileStructureContext from './context/EnterpriseProfileStructureContext';
import EnterpriseAvatar from './general/EnterpriseAvatar';
import EnterpriseCover from './general/EnterpriseCover';
import EnterpriseFollowerSection from './general/EnterpriseFollowSection';

const EnterpriseFollowButton = dynamic(
  () => import('@/components/buttons/user/EnterpriseFollowButton')
);
const EditBusinessInfoModal = dynamic(
  () => import('@/components/modal/training-module/EditBusinessInfoModal')
);

const EnterpriseGeneralInformation = ({
  profileData,
}: {
  profileData: IUserProfileData | undefined;
}) => {
  const { isDesktop } = useScreen();
  const { isMatchMaxWidth } = useDynamicScreen({
    maxWidth: 1025,
  });
  const { toast } = useModal();
  const profiles = profileData?.profiles;

  const canEdit = profileData?.viewPermission === RoleViewEnum.Owner;
  const { structure } = useContext(EnterpriseProfileStructureContext);

  const elementHomepageTabNavigateButtonSection =
    useMemo((): TEnterpriseProfileHomepageTabSection[0] => {
      const homepageTab: IEnterpriseProfileStructureJSON['structure'][0] =
        structure.find((item) => item.tabKey === EEnterpriseTabKey.HomePage)!;

      if (!homepageTab) return {} as TEnterpriseProfileHomepageTabSection[0];

      const { sections } = homepageTab;
      const featuredTopicsSection = (
        sections as TEnterpriseProfileHomepageTabSection
      )?.find(
        (section) =>
          section.sectionKey === EHomepageTabSectionKey.NavigateButton
      );

      if (!featuredTopicsSection)
        return {} as TEnterpriseProfileHomepageTabSection[0];

      return featuredTopicsSection as TEnterpriseProfileHomepageTabSection[0];
    }, [structure]);

  const { isActive, elements } = elementHomepageTabNavigateButtonSection;

  return (
    <>
      <div className="relative z-[9] bg-Other/White">
        <EnterpriseCover cover={profiles?.cover || null} canEdit={canEdit} />
        <EnterpriseAvatar
          canEdit={canEdit}
          avatarUrl={profiles?.avatar || null}
        />
      </div>
      <div className={`relative p-4 ${!isDesktop && '!p-0 !pt-[2px]'}`}>
        <div
          className={`absolute top-0 z-[8] h-4 w-[90%] bg-Other/White ${
            !isDesktop && 'h-[2px]'
          }`}
        ></div>
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 transform rounded-full border border-Primary/Other(Border) bg-Other/White p-6">
          <div
            className={`aspect-square w-[200px] ${!isDesktop && 'w-[40px]'}`}
          ></div>
        </div>
        <div className="flex justify-between rounded-[10px] border border-Primary/Other(Border) p-3 shadow-[0px_10px_20px_rgba(0,0,0,0.07)] md:p-6">
          <div className="min-h-[97px] w-[39%]">
            <div className="mb-[2px] flex items-center gap-1 md:gap-2">
              <h1
                className={`text-2xl font-semibold leading-[140%] text-Text/Lightbackground/Primary break-word-legacy ${
                  !isDesktop && 'font-semibolds text-[18px] text-[#242424]'
                }`}
              >
                {profiles?.title && <>{profiles.title}. </>}
                {profiles?.qbankData.fullName}
              </h1>
              {profiles?.hasBlueTick && <BlueTick scale="100%" />}
            </div>
            <Description bio={profiles?.bio || ''} />
          </div>
          <div className="flex flex-col justify-between">
            <EnterpriseFollowerSection profileData={profileData} />
            <div className="flex items-center justify-end gap-2">
              {isActive && (
                <DSButton
                  onClick={() =>
                    window.open((elements as any)?.navigate?.url || '')
                  }
                  mode={isDesktop ? 'text-only' : 'icon-only'}
                  size={isDesktop ? 'medium' : 'small'}
                  startIcon={isDesktop ? { name: 'member' } : undefined}
                  icon={isDesktop ? undefined : { name: 'member' }}
                  emphasis="high"
                  className={`${isMatchMaxWidth && 'max-w-[170px]'}`}
                >
                  {isDesktop && (
                    <span className="truncate">
                      {(elements as any)?.navigate?.title || ''}
                    </span>
                  )}
                </DSButton>
              )}
              <EnterpriseControlButton profileData={profileData} />
              <DSButton
                onClick={() =>
                  handleShare(profiles?.qbankData.fullName || '', toast)
                }
                emphasis="medium"
                size={isDesktop ? 'medium' : 'small'}
                mode="icon-only"
              >
                <OutlinedSharedIcon
                  height={isDesktop ? 20 : 16}
                  width={isDesktop ? 20 : 16}
                />
              </DSButton>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function Description({ bio }: { bio: string }) {
  const { isDesktop } = useScreen();

  const { contentRef, maxHeight, setMaxHeight } = useParagraphLineClamped({
    linesAllowed: 3,
  });
  //use the logic below to ensure that after change the description, isclamped behavior still true
  const [isClamped, setIsClamped] = useState<boolean>(false);
  useEffect(() => {
    setIsClamped(
      typeof maxHeight === 'number'
        ? (contentRef.current?.scrollHeight as number) > maxHeight
        : false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bio, maxHeight]);

  return (
    <div
      className={`relative text-sm font-medium leading-[120%] text-Text/Lightbackground/Secondary ${
        !isDesktop && '!text-[12px] font-normal leading-[16.8px]'
      }`}
    >
      <p
        ref={contentRef}
        style={{
          maxHeight: maxHeight,
          overflowY: 'hidden',
        }}
        className="!whitespace-pre-wrap"
      >
        {bio || <i>Chưa có mô tả</i>}
      </p>
      {isClamped && (
        <>
          <span className="inline">... </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              setMaxHeight('none');
            }}
            className="inline cursor-pointer text-Primary/Main hover:underline"
          >
            Xem thêm
          </span>
        </>
      )}
    </div>
  );
}

function EnterpriseControlButton({
  profileData,
}: {
  profileData: IUserProfileData | undefined;
}) {
  const [openEditModal, setOpenEditModal] = useState(false);
  const { isDesktop } = useScreen();

  const name = profileData?.profiles?.qbankData?.fullName || '';
  const description = profileData?.profiles?.bio || '';

  const toggleEditModal = () => {
    setOpenEditModal((_) => !_);
  };

  return (
    <>
      {profileData?.viewPermission === RoleViewEnum.Owner ? (
        <DSButton
          onClick={toggleEditModal}
          emphasis="medium"
          mode={isDesktop ? 'text-only' : 'icon-only'}
          size={isDesktop ? 'medium' : 'small'}
        >
          <OutlinedPencil
            height={isDesktop ? 20 : 16}
            width={isDesktop ? 20 : 16}
            className={isDesktop ? 'mr-2' : ''}
          />
          {isDesktop && <p>Chỉnh sửa</p>}
        </DSButton>
      ) : (
        <EnterpriseFollowButton
          userId={`${profileData?.profiles?.ownerId || ''}`}
          isFollowed={profileData?.viewPermission === RoleViewEnum.Follow}
        />
      )}

      <EditBusinessInfoModal
        onClose={toggleEditModal}
        open={openEditModal}
        defaultData={{
          name,
          description,
        }}
      />
    </>
  );
}

export default EnterpriseGeneralInformation;
